import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WebApp from "@twa-dev/sdk";
import { FaLink, FaSteam } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import useUserStore from '../stores/userStore';

const API_URL = process.env.REACT_APP_API_URL;

const Container = styled.div`
    min-height: 100vh;
    background: ${props => props.theme.background};
    display: flex;
    flex-direction: column;
`;

const Content = styled.div`
    padding: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
`;

const SteamLogo = styled(FaSteam)`
    color: ${props => props.theme.text.primary};
    opacity: 0.8;
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 20px;
`;

const Description = styled.p`
    font-size: 14px;
    color: ${props => props.theme.text.secondary};
    margin: 0;
    line-height: 1.5;
    text-align: center;
`;

const InputContainer = styled.div`
    margin-bottom: 16px;
`;

const TradeUrlInput = styled.input`
    width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 12px 16px;
    color: ${props => props.theme.text.primary};
    font-size: 14px;

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
        outline: none;
        border-color: ${props => props.theme.primary};
        background: rgba(255, 255, 255, 0.07);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const SaveButton = styled.button`
    background: ${props => props.theme.primary};
    color: white;
    border: none;
    border-radius: 12px;
    padding: 14px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    width: 100%;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const InfoSection = styled.div`
    margin-top: 16px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
`;

const InfoTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.text.primary};
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const InfoText = styled.p`
    font-size: 14px;
    color: ${props => props.theme.text.secondary};
    margin: 0;
    line-height: 1.6;
`;

const HelpButton = styled.button`
    background: none;
    border: none;
    color: #0095ff;
    font-size: 14px;
    cursor: pointer;
    padding: 8px 0;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    &:hover {
        opacity: 0.8;
    }
`;

function TradeUrlPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setUser = useUserStore.setState;
  const [tradeUrl, setTradeUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const fetchTradeUrl = async () => {
      if (!isFirstRender.current) return;
      isFirstRender.current = false;

      try {
        setIsLoading(true);
        const response = await apiService.getTradeUrl();
        console.log('Trade URL response:', response);

        if (response && response.tradeUrl) {
          console.log('Setting trade URL:', response.tradeUrl);
          setTradeUrl(response.tradeUrl);
          setIsValidUrl(Boolean(response.tradeUrl));
        } else {
          console.warn('Invalid response format:', response);
          setTradeUrl('');
          setIsValidUrl(true);
        }
      } catch (error) {
        console.error('Error fetching trade URL:', error);
        toast.error('Ошибка при получении Trade URL');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTradeUrl();
  }, []);

  useEffect(() => {
    if (WebApp.platform !== 'unknown') {
      WebApp.BackButton.show();
      WebApp.BackButton.onClick(() => {
        navigate(-1);
      });
    }
    return () => {
      if (WebApp.platform !== 'unknown') {
        WebApp.BackButton.hide();
        WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  const validateTradeUrl = (url) => {
    const steamTradeUrlRegex = /^https:\/\/steamcommunity\.com\/tradeoffer\/new\/\?partner=\d+&token=[a-zA-Z0-9_-]+$/;
    return steamTradeUrlRegex.test(url);
  };

  const handleTradeUrlChange = (e) => {
    const url = e.target.value;
    setTradeUrl(url);
    setIsValidUrl(validateTradeUrl(url));
  };

  const handleSave = async () => {
    if (!validateTradeUrl(tradeUrl)) {
      toast.error(t('common.error'));
      return;
    }

    setIsSaving(true);

    try {
      const response = await apiService.updateTradeUrl(tradeUrl);

      if (response.success) {
        setUser(prev => ({
          ...prev,
          trade_url: tradeUrl
        }));
        toast.success(t('common.success'), {
          position: "top-center",
          theme: "dark"
        });
      } else {
        throw new Error('Failed to save trade URL');
      }
    } catch (error) {
      console.error('Error saving trade URL:', error);
      toast.error(t('common.error'), {
        position: "top-center",
        theme: "dark"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleHowToGet = () => {
    const steamUrl = 'https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url';
    if (WebApp.platform !== "unknown") {
      WebApp.openLink(steamUrl);
    } else {
      window.open(steamUrl, '_blank');
    }
  };

  return (
    <Container>
      <Content>
        <LogoContainer>
          <SteamLogo size={80} />
        </LogoContainer>

        <FormSection>
          <Description>
            {t('tradeUrl.description')}
          </Description>

          <InputContainer>
            <TradeUrlInput
              value={tradeUrl}
              onChange={handleTradeUrlChange}
              placeholder="https://steamcommunity.com/tradeoffer/new/?partner=..."
              isValid={isValidUrl}
              disabled={isLoading}
            />
            <HelpButton onClick={handleHowToGet}>
              <FaSteam size={16} />
              {t('tradeUrl.howToGet')}
            </HelpButton>
          </InputContainer>

          <SaveButton
            onClick={handleSave}
            disabled={!isValidUrl || isSaving || !tradeUrl || isLoading}
          >
            {isLoading ? t('tradeUrl.loading') : isSaving ? t('tradeUrl.saving') : t('tradeUrl.save')}
          </SaveButton>

          <InfoSection>
            <InfoTitle>
              <FaLink size={18} />
              {t('tradeUrl.instruction')}
            </InfoTitle>
            <InfoText>
              {t('tradeUrl.steps.1')}<br />
              {t('tradeUrl.steps.2')}<br />
              {t('tradeUrl.steps.3')}<br />
              {t('tradeUrl.steps.4')}<br />
              {t('tradeUrl.steps.5')}
            </InfoText>
          </InfoSection>
        </FormSection>
      </Content>
    </Container>
  );
}

export default TradeUrlPage; 